import type { FinancialDatum } from "@shared/member-financials";

function displayedValue(datum?: FinancialDatum): string | null {
  if (!datum || (datum.state !== "observed" && datum.state !== "stale")) return null;
  const value = String(datum.display ?? "").trim();
  return !value || /^(unknown|temporarily unavailable)$/i.test(value) ? null : value;
}

/** Only the reader's explicit credit-positive amount/currency format is money. */
function balanceValue(datum?: FinancialDatum): { amount: number; currency: string } | null {
  const value = displayedValue(datum);
  const match = value?.match(/^(-?\d+\.\d{2}) ([A-Z]{3})$/);
  if (!match) return null;
  const amount = Number(match[1]);
  return Number.isFinite(amount) ? { amount, currency: match[2] } : null;
}

/** Missing/rejected evidence stays last in both directions; ties keep input order. */
export function compareFinancialEvidence(
  a: FinancialDatum | undefined,
  b: FinancialDatum | undefined,
  field: "financial_status" | "balance",
  direction: "asc" | "desc",
): number {
  const aValue = field === "balance" ? balanceValue(a) : displayedValue(a);
  const bValue = field === "balance" ? balanceValue(b) : displayedValue(b);
  if (aValue === null) return bValue === null ? 0 : 1;
  if (bValue === null) return -1;
  // Different currencies are grouped, never converted or treated as equivalent.
  const comparison = typeof aValue === "string" && typeof bValue === "string"
    ? aValue.toLowerCase().localeCompare(bValue.toLowerCase())
    : typeof aValue === "object" && typeof bValue === "object"
      ? aValue.currency.localeCompare(bValue.currency) || aValue.amount - bValue.amount
      : 0;
  return direction === "asc" ? comparison : -comparison;
}
